import React, { useEffect, useState } from 'react'
import { ArrowLeft, Plus, Trash2, ClipboardList, ChevronRight, Presentation, Eye, MessageSquare, ExternalLink, Lock, Crown, FileDown, Bell, Pencil, X, Save } from 'lucide-react'
import { teacherGroupApi, type TActivity, type TObservation } from './api'

// Sinh hoạt chuyên môn theo Nghiên cứu bài học (NCBH) — năng lực 5.2.
const PHASES: { key: TActivity['phase']; label: string }[] = [
  { key: 'design', label: 'Cùng thiết kế' },
  { key: 'teach', label: 'Dạy minh hoạ' },
  { key: 'observe', label: 'Dự giờ' },
  { key: 'reflect', label: 'Rút kinh nghiệm' },
  { key: 'done', label: 'Hoàn thành' },
]

const PHASE_GUIDES: Record<TActivity['phase'], { title: string; goal: string; tasks: string[]; evidence: string[] }> = {
  design: {
    title: '1. Cùng thiết kế bài học',
    goal: 'Thống nhất vấn đề cần nghiên cứu, mục tiêu năng lực học sinh và kịch bản tổ chức hoạt động học.',
    tasks: ['Xác định bài/chủ đề, lớp dạy, đối tượng học sinh.', 'Thống nhất câu hỏi nghiên cứu: học sinh sẽ gặp khó khăn gì, cần quan sát biểu hiện nào.', 'Phân công người chuẩn bị học liệu, câu hỏi, phiếu học tập, thiết bị hoặc nguồn số.'],
    evidence: ['Kế hoạch bài dạy hoặc link học liệu.', 'Phiếu giao nhiệm vụ/câu hỏi nghiên cứu.', 'Phân công thành viên trong tổ.'],
  },
  teach: {
    title: '2. Dạy minh hoạ',
    goal: 'Tổ chức tiết dạy theo kịch bản đã thống nhất để thu thập minh chứng về việc học của học sinh.',
    tasks: ['Giáo viên dạy minh hoạ thực hiện bài học theo kế hoạch.', 'Thành viên dự giờ không đánh giá giáo viên, tập trung quan sát học sinh.', 'Ghi lại tình huống học tập, sản phẩm học sinh, điểm cần điều chỉnh.'],
    evidence: ['Thông tin giáo viên dạy minh hoạ và thời gian.', 'Ảnh/sản phẩm học tập nếu có.', 'Ghi chú nhanh các tình huống đáng chú ý.'],
  },
  observe: {
    title: '3. Dự giờ, quan sát học sinh',
    goal: 'Thu thập phiếu quan sát về biểu hiện học tập, khó khăn và mức độ đạt mục tiêu của học sinh.',
    tasks: ['Mỗi thành viên ghi phiếu dự giờ theo nhóm học sinh hoặc hoạt động được phân công.', 'Tập trung vào minh chứng: học sinh làm gì, nói gì, sản phẩm ra sao.', 'Nêu khó khăn và đề xuất điều chỉnh dựa trên dữ liệu quan sát.'],
    evidence: ['Phiếu dự giờ của thành viên.', 'Minh chứng sản phẩm/trao đổi của học sinh.', 'Danh sách vấn đề cần phân tích sau tiết dạy.'],
  },
  reflect: {
    title: '4. Rút kinh nghiệm, cải tiến',
    goal: 'Phân tích minh chứng dự giờ để điều chỉnh bài học, học liệu và cách tổ chức hoạt động.',
    tasks: ['Tổng hợp điểm nổi bật, khó khăn và đề xuất từ phiếu dự giờ.', 'Thảo luận nguyên nhân dựa trên biểu hiện học sinh, không quy kết cá nhân.', 'Chốt phương án cải tiến cho lần dạy sau hoặc cho bài tương tự.'],
    evidence: ['Biên bản rút kinh nghiệm.', 'Danh sách điều chỉnh bài học/học liệu.', 'Khuyến nghị dùng lại/chia sẻ trong tổ.'],
  },
  done: {
    title: '5. Hoàn thành, chia sẻ',
    goal: 'Lưu hồ sơ chuyên đề, xuất biên bản và chia sẻ kết quả cải tiến cho tổ chuyên môn.',
    tasks: ['Kiểm tra đủ thông tin chuyên đề, phiếu dự giờ và kết luận cải tiến.', 'Xuất biên bản Word/PDF để lưu minh chứng.', 'Chia sẻ học liệu hoặc kinh nghiệm có thể dùng lại cho các môn/chủ đề phù hợp.'],
    evidence: ['Biên bản chuyên đề.', 'Hồ sơ phiếu dự giờ.', 'Học liệu hoặc khuyến nghị đã hoàn thiện.'],
  },
}

const phaseIdx = (p: string) => Math.max(0, PHASES.findIndex((x) => x.key === p))

const NcbhTab: React.FC<{ gid: string; isPro: boolean; onUpgrade: () => void }> = ({ gid, isPro, onUpgrade }) => {
  const [items, setItems] = useState<TActivity[]>([])
  const [open, setOpen] = useState<string>('')
  const [form, setForm] = useState({ title: '', description: '', lesson_ref: '', lesson_title: '', demo_teacher_name: '', scheduled_at: '' })
  const load = () => teacherGroupApi.listActivities(gid).then(setItems).catch(() => setItems([]))
  useEffect(() => { if (isPro) load() }, [gid, isPro])

  if (!isPro) {
    return (
      <div className="bg-white rounded-2xl border border-amber-100 p-6 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-100 text-amber-700 mb-2"><Lock size={22} /></div>
        <div className="font-black text-slate-800">Chuyên đề NCBH — thuộc Gói Tổ PRO</div>
        <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">Sinh hoạt chuyên môn theo Nghiên cứu bài học: cùng thiết kế → dạy minh hoạ → dự giờ (phiếu quan sát việc học của HS) → rút kinh nghiệm.</p>
        <button onClick={onUpgrade} className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500 text-white text-sm font-bold hover:bg-amber-600"><Crown size={15} /> Nâng cấp Gói Tổ</button>
      </div>
    )
  }

  const create = async () => {
    if (!form.title.trim()) return
    const a = await teacherGroupApi.createActivity(gid, form)
    setForm({ title: '', description: '', lesson_ref: '', lesson_title: '', demo_teacher_name: '', scheduled_at: '' })
    load(); setOpen(a.id)
  }

  if (open) return <ActivityDetail aid={open} onBack={() => { setOpen(''); load() }} />

  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-teal-100 bg-teal-50/60 p-3 text-sm text-slate-600">
        <b className="text-teal-800">5.2 Hợp tác phát triển chuyên môn:</b> mỗi chuyên đề đi theo chu trình cùng thiết kế → dạy minh hoạ → dự giờ quan sát việc học của học sinh → rút kinh nghiệm. Dùng được cho mọi môn, không phụ thuộc kho học liệu Vật lí.
      </div>
      <div className="bg-white rounded-xl border border-gray-100 p-3">
        <div className="font-bold text-sm text-slate-700 mb-2 flex items-center gap-1.5"><ClipboardList size={15} className="text-teal-600" /> Tạo chuyên đề NCBH mới</div>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Tên chuyên đề (vd: NCBH bài Định luật Ohm)" className="w-full mb-2 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200" />
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Mục tiêu / nội dung nghiên cứu bài học…" className="w-full mb-2 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200 resize-y" />
        <div className="flex flex-wrap gap-2 mb-2">
          <input value={form.lesson_title} onChange={(e) => setForm({ ...form, lesson_title: e.target.value })} placeholder="Bài giảng cùng thiết kế (tên)" className="flex-1 min-w-[160px] p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200" />
          <input value={form.lesson_ref} onChange={(e) => setForm({ ...form, lesson_ref: e.target.value })} placeholder="slug/ID bài giảng" className="w-40 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200" />
        </div>
        <div className="flex flex-wrap gap-2 mb-2">
          <input value={form.demo_teacher_name} onChange={(e) => setForm({ ...form, demo_teacher_name: e.target.value })} placeholder="GV dạy minh hoạ" className="flex-1 min-w-[160px] p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200" />
          <input value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} placeholder="Thời gian dự giờ" className="w-44 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200" />
        </div>
        <button onClick={create} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-bold hover:bg-teal-700"><Plus size={15} /> Tạo chuyên đề</button>
      </div>

      {items.length === 0 ? <p className="text-sm text-gray-400 p-3 text-center">Chưa có chuyên đề NCBH nào.</p>
        : items.map((a) => (
          <button key={a.id} onClick={() => setOpen(a.id)} className="w-full text-left bg-white rounded-xl border border-gray-100 hover:border-teal-300 p-3 flex items-center gap-3">
            <ClipboardList size={18} className="text-teal-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm text-slate-800 truncate">{a.title}</div>
              <div className="text-xs text-slate-400">{PHASES[phaseIdx(a.phase)]?.label} · {a.demo_teacher_name || 'chưa phân công'} · {a.obsCount} phiếu dự giờ</div>
            </div>
            <ChevronRight size={16} className="text-slate-300" />
          </button>
        ))}
    </div>
  )
}

const ActivityDetail: React.FC<{ aid: string; onBack: () => void }> = ({ aid, onBack }) => {
  const [a, setA] = useState<(TActivity & { observations: TObservation[] }) | null>(null)
  const [obs, setObs] = useState({ strengths: '', difficulties: '', suggestions: '' })
  const [msg, setMsg] = useState('')
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState({ title: '', description: '', lesson_ref: '', lesson_title: '', demo_teacher_name: '', scheduled_at: '' })
  const load = () => teacherGroupApi.activityDetail(aid).then(setA).catch(() => setA(null))
  useEffect(() => { load() }, [aid])
  useEffect(() => {
    if (!a || editing) return
    setEditForm({
      title: a.title || '', description: a.description || '', lesson_ref: a.lesson_ref || '',
      lesson_title: a.lesson_title || '', demo_teacher_name: a.demo_teacher_name || '', scheduled_at: a.scheduled_at || '',
    })
  }, [a?.id, editing])
  if (!a) return <p className="text-sm text-gray-400">Đang tải…</p>

  const notify = async () => { try { const r = await teacherGroupApi.notifySchedule(aid); setMsg(`✅ Đã gửi nhắc dự giờ tới ${r.sent} email.`) } catch { setMsg('Gửi nhắc chưa thành công.') } }

  const startEdit = () => {
    setEditForm({
      title: a.title || '', description: a.description || '', lesson_ref: a.lesson_ref || '',
      lesson_title: a.lesson_title || '', demo_teacher_name: a.demo_teacher_name || '', scheduled_at: a.scheduled_at || '',
    })
    setEditing(true)
  }
  const saveEdit = async () => {
    if (!editForm.title.trim()) { setMsg('Tên chuyên đề không được trống.'); return }
    try {
      await teacherGroupApi.updateActivity(aid, editForm)
      setEditing(false)
      setMsg('✅ Đã lưu thay đổi chuyên đề.')
      load()
    } catch {
      setMsg('Không thể sửa chuyên đề. Bạn cần là người tạo, tổ trưởng hoặc quản trị viên.')
    }
  }
  const deleteActivity = async () => {
    if (!confirm(`Xóa chuyên đề "${a.title}"? Các phiếu dự giờ liên quan sẽ không còn hiển thị trong chuyên đề này.`)) return
    try {
      await teacherGroupApi.delActivity(aid)
      onBack()
    } catch {
      setMsg('Không thể xóa chuyên đề. Bạn cần là người tạo, tổ trưởng hoặc quản trị viên.')
    }
  }

  const setPhase = async (p: TActivity['phase']) => { await teacherGroupApi.updateActivity(aid, { phase: p }); load() }
  const submitObs = async () => {
    if (!obs.strengths.trim() && !obs.difficulties.trim() && !obs.suggestions.trim()) return
    await teacherGroupApi.addObservation(aid, obs); setObs({ strengths: '', difficulties: '', suggestions: '' }); load()
  }
  const cur = phaseIdx(a.phase)

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800"><ArrowLeft size={15} /> Danh sách chuyên đề</button>

      <div className="bg-white rounded-2xl border border-teal-100 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-black text-lg text-slate-900">{a.title}</div>
            {a.description && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{a.description}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={startEdit} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-teal-300 text-teal-700 text-sm font-bold hover:bg-teal-50"><Pencil size={14} /> Sửa</button>
            <button onClick={deleteActivity} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-rose-200 text-rose-600 text-sm font-bold hover:bg-rose-50"><Trash2 size={14} /> Xóa</button>
          </div>
        </div>

        {editing && (
          <div className="mt-3 rounded-xl border border-teal-100 bg-teal-50/40 p-3">
            <div className="font-bold text-sm text-slate-700 mb-2">Sửa thông tin chuyên đề</div>
            <input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} placeholder="Tên chuyên đề" className="w-full mb-2 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200 bg-white" />
            <textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={2} placeholder="Mục tiêu / nội dung nghiên cứu bài học" className="w-full mb-2 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200 bg-white resize-y" />
            <div className="flex flex-wrap gap-2 mb-2">
              <input value={editForm.lesson_title} onChange={(e) => setEditForm({ ...editForm, lesson_title: e.target.value })} placeholder="Bài giảng cùng thiết kế" className="flex-1 min-w-[160px] p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200 bg-white" />
              <input value={editForm.lesson_ref} onChange={(e) => setEditForm({ ...editForm, lesson_ref: e.target.value })} placeholder="slug/ID bài giảng" className="w-40 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200 bg-white" />
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              <input value={editForm.demo_teacher_name} onChange={(e) => setEditForm({ ...editForm, demo_teacher_name: e.target.value })} placeholder="GV dạy minh hoạ" className="flex-1 min-w-[160px] p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200 bg-white" />
              <input value={editForm.scheduled_at} onChange={(e) => setEditForm({ ...editForm, scheduled_at: e.target.value })} placeholder="Thời gian dự giờ" className="w-44 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200 bg-white" />
            </div>
            <div className="flex items-center gap-2">
              <button onClick={saveEdit} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-600 text-white text-sm font-bold hover:bg-teal-700"><Save size={14} /> Lưu</button>
              <button onClick={() => setEditing(false)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-slate-600 text-sm font-bold hover:bg-gray-50"><X size={14} /> Hủy</button>
            </div>
          </div>
        )}

        <div className="text-xs text-slate-500 mt-2 flex flex-wrap gap-x-3 gap-y-1">
          {a.lesson_title && <span className="inline-flex items-center gap-1"><Presentation size={12} /> {a.lesson_title}{a.lesson_ref && <a href={`/bai-giang/${a.lesson_ref}`} target="_blank" rel="noreferrer" className="text-sky-600"><ExternalLink size={11} /></a>}</span>}
          {a.demo_teacher_name && <span>GV dạy minh hoạ: {a.demo_teacher_name}</span>}
          {a.scheduled_at && <span>Thời gian: {a.scheduled_at}</span>}
        </div>

        {/* 5 bước NCBH */}
        <div className="flex flex-wrap items-center gap-1.5 mt-3">
          {PHASES.map((p, i) => (
            <button key={p.key} onClick={() => setPhase(p.key)}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border transition ${i <= cur ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-500 border-gray-200 hover:border-teal-300'}`}>
              {i + 1}. {p.label}
            </button>
          ))}
        </div>

        <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-5">
          {PHASES.map((p, i) => {
            const guide = PHASE_GUIDES[p.key]
            const active = a.phase === p.key
            return (
              <div key={p.key} className={`rounded-xl border p-3 text-sm ${active ? 'border-teal-300 bg-teal-50' : 'border-gray-100 bg-gray-50/70'}`}>
                <div className={`font-black mb-1 ${active ? 'text-teal-800' : 'text-slate-700'}`}>{guide.title}</div>
                <p className="text-xs text-slate-600 leading-relaxed mb-2">{guide.goal}</p>
                <div className="text-[11px] font-bold uppercase text-slate-400 mb-1">Việc cần làm</div>
                <ul className="space-y-1 text-xs text-slate-600 list-disc pl-4">
                  {guide.tasks.map((task) => <li key={task}>{task}</li>)}
                </ul>
                <div className="text-[11px] font-bold uppercase text-slate-400 mt-2 mb-1">Minh chứng</div>
                <ul className="space-y-1 text-xs text-slate-600 list-disc pl-4">
                  {guide.evidence.map((item) => <li key={item}>{item}</li>)}
                </ul>
                {active && <div className="mt-2 inline-flex px-2 py-0.5 rounded-full bg-teal-600 text-white text-[11px] font-bold">Đang thực hiện bước {i + 1}</div>}
              </div>
            )
          })}
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100">
          <button onClick={notify} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-600 text-white text-sm font-bold hover:bg-sky-700"><Bell size={14} /> Gửi email nhắc dự giờ</button>
          <button onClick={() => teacherGroupApi.exportActivity(aid, 'docx').catch(() => {})} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-sky-300 text-sky-700 text-sm font-bold hover:bg-sky-50"><FileDown size={14} /> Biên bản (Word)</button>
          <button onClick={() => teacherGroupApi.exportActivity(aid, 'pdf').catch(() => {})} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-sky-300 text-sky-700 text-sm font-bold hover:bg-sky-50"><FileDown size={14} /> PDF</button>
          {msg && <span className="text-xs text-emerald-700">{msg}</span>}
        </div>
      </div>

      {/* Phiếu dự giờ — quan sát việc học của HS (không chấm GV) */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <div className="font-bold text-slate-800 mb-2 flex items-center gap-1.5"><Eye size={16} className="text-teal-600" /> Ghi phiếu dự giờ <span className="text-xs font-normal text-slate-400">(quan sát việc HỌC của học sinh)</span></div>
        <textarea value={obs.strengths} onChange={(e) => setObs({ ...obs, strengths: e.target.value })} rows={2} placeholder="Điểm nổi bật: HS học được gì, biểu hiện tích cực…" className="w-full mb-2 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200 resize-y" />
        <textarea value={obs.difficulties} onChange={(e) => setObs({ ...obs, difficulties: e.target.value })} rows={2} placeholder="Khó khăn HS gặp phải khi học…" className="w-full mb-2 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200 resize-y" />
        <textarea value={obs.suggestions} onChange={(e) => setObs({ ...obs, suggestions: e.target.value })} rows={2} placeholder="Đề xuất điều chỉnh bài học/hoạt động…" className="w-full mb-2 p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-200 resize-y" />
        <button onClick={submitObs} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-bold hover:bg-teal-700"><MessageSquare size={15} /> Gửi phiếu</button>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-bold text-slate-500 uppercase">Phiếu dự giờ của tổ ({a.observations.length})</div>
        {a.observations.length === 0 ? <p className="text-sm text-gray-400">Chưa có phiếu nào.</p>
          : a.observations.map((o) => (
            <div key={o.id} className="bg-white rounded-xl border border-gray-100 p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-slate-800">{o.observer_name}</span>
                <button onClick={async () => { await teacherGroupApi.delObservation(o.id); load() }} className="text-rose-300 hover:text-rose-600"><Trash2 size={13} /></button>
              </div>
              {o.strengths && <p className="text-sm text-slate-700 mt-1"><b className="text-emerald-700">Nổi bật:</b> {o.strengths}</p>}
              {o.difficulties && <p className="text-sm text-slate-700 mt-0.5"><b className="text-amber-700">Khó khăn:</b> {o.difficulties}</p>}
              {o.suggestions && <p className="text-sm text-slate-700 mt-0.5"><b className="text-sky-700">Đề xuất:</b> {o.suggestions}</p>}
            </div>
          ))}
      </div>
    </div>
  )
}

export default NcbhTab

